import { motion, useMotionValue, animate } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconHeart, IconHeartFilled, IconEyeOff, IconMessageCircle } from '@tabler/icons-react';
import { compact } from '@/lib/format';
import type { ShelfItem } from '@/api/types';
import { api } from '@/api/endpoints';
import { haptic } from '@/lib/telegram';
import { OpenBookConfirm } from '@/features/shelf/OpenBookConfirm';

interface Props {
  item: ShelfItem;
  onHide: (cardId: string, hide: boolean) => void;
  onDelete: (cardId: string) => void;
}

const ACTION_W = 160;

export function ShelfRow({ item, onHide, onDelete }: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const x = useMotionValue(0);
  const [revealed, setRevealed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [liked, setLiked] = useState(item.user_actions?.liked ?? false);
  const [likes, setLikes] = useState(item.likes);

  const close = () => {
    animate(x, 0, { type: 'spring', stiffness: 380, damping: 32 });
    setRevealed(false);
  };

  const bind = useDrag(({ down, movement: [mx], last }) => {
    if (down) {
      x.set(Math.max(-ACTION_W, Math.min(0, mx + (revealed ? -ACTION_W : 0))));
      return;
    }
    if (last) {
      const open = Math.abs(x.get()) > ACTION_W / 2;
      x.set(open ? -ACTION_W : 0);
      setRevealed(open);
    }
  }, { axis: 'x' });

  const onLike = async (e: MouseEvent) => {
    e.stopPropagation();
    haptic.light();
    const next = !liked;
    setLiked(next);
    setLikes(v => Math.max(0, v + (next ? 1 : -1)));
    try {
      await api.cards.toggle(item.id, 'like');
    } catch {
      setLiked(!next);
      setLikes(v => Math.max(0, v + (next ? -1 : 1)));
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Action layer */}
      <div className="absolute inset-y-0 right-0 flex" style={{ width: ACTION_W }}>
        <button
          className="flex-1 flex flex-col items-center justify-center bg-warning text-white text-xs text-center px-2 leading-tight"
          onClick={() => { onHide(item.id, !item.hidden_from_public); close(); }}
        >
          <IconEyeOff size={18} />
          <span className="mt-1">{t('shelf.hide_for_all')}</span>
        </button>
        <button
          className="flex-1 flex flex-col items-center justify-center bg-danger text-white text-xs"
          onClick={() => { onDelete(item.id); close(); }}
        >
          ✕ {t('shelf.delete')}
        </button>
      </div>

      <motion.div
        {...(bind() as Record<string, unknown>)}
        style={{ x, touchAction: 'pan-y' }}
        onClick={() => {
          if (revealed) { close(); return; }
          haptic.light();
          setConfirmOpen(true);
        }}
        className="relative bg-surface p-4"
      >
        <div>
          <p className="font-content text-base leading-snug text-text">{item.text}</p>
          <div className="mt-2">
            <div className="text-sm italic text-text leading-tight break-words">{item.book_title ?? t('shelf.no_title')}</div>
            {item.book_author && (
              <div className="text-xs text-text-muted break-words">{item.book_author}</div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-end gap-3 text-xs text-text-muted">
            {item.hidden_from_public && (
              <span className="mr-auto flex items-center gap-1 text-[11px] uppercase tracking-wider">
                <IconEyeOff size={14} />
                <span>{t('shelf.hidden_label')}</span>
              </span>
            )}
            <button
              onClick={onLike}
              className="flex items-center gap-1 hover:text-danger transition-colors"
              aria-label={t('shelf.like', { defaultValue: 'Like' })}
            >
              {liked ? <IconHeartFilled size={16} className="text-danger" /> : <IconHeart size={16} />}
              <span>{compact(likes)}</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); haptic.light(); nav(`/card/${item.id}/comments`); }}
              className="flex items-center gap-1 hover:text-text transition-colors"
              aria-label={t('feed.comments', { defaultValue: 'Comments' })}
            >
              <IconMessageCircle size={16} />
              <span>{compact(item.comments ?? 0)}</span>
            </button>
          </div>
        </div>
      </motion.div>
      <OpenBookConfirm
        open={confirmOpen}
        bookTitle={item.book_title}
        bookAuthor={item.book_author}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          nav(`/book/${item.book_id}`, { state: { from: '/shelf' } });
        }}
      />
    </div>
  );
}
